import React, { useEffect, useState, useCallback } from 'react';
import { fuel as fuelApi, trips as tripsApi, extractError } from '../services/api';
import { Modal } from '../components/ui/Modal';
import { TableSkeleton } from '../components/ui/LoadingSpinner';
import { useToast } from '../store/ToastContext';
import { useAuth } from '../store/AuthContext';

const PAYMENT_METHODS = ['fuel_card','cash','credit_card','company_account'];

function FuelForm({ onSave, onClose }) {
  const toast = useToast();
  const [form, setForm] = useState({
    trip_id: '', vehicle_id: '', gallons: '', price_per_gallon: '',
    total_cost: '', fuel_timestamp: new Date().toISOString().slice(0,16),
    location_address: '', fuel_station_name: '', payment_method: 'fuel_card',
    odometer_reading: '',
  });
  const [trips, setTrips]   = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    tripsApi.list({ status: 'in_progress' })
      .then(data => setTrips(Array.isArray(data) ? data : data?.data || []))
      .catch(() => {});
  }, []);

  const set = (k, v) => {
    setForm(f => {
      const nf = { ...f, [k]: v };
      // Auto-calc total cost
      if ((k === 'gallons' || k === 'price_per_gallon') && nf.gallons && nf.price_per_gallon) {
        nf.total_cost = (parseFloat(nf.gallons) * parseFloat(nf.price_per_gallon)).toFixed(2);
      }
      return nf;
    });
    if (errors[k]) setErrors(e => ({...e,[k]:undefined}));
  };

  const validate = () => {
    const e = {};
    if (!form.trip_id)  e.trip_id  = 'Required';
    if (!form.gallons)  e.gallons  = 'Required';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // Get vehicle_id from selected trip
      const trip = trips.find(t => t.id === form.trip_id);
      await onSave({ ...form, vehicle_id: trip?.vehicle_id || form.vehicle_id });
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="label">Trip *</label>
        <select className={`input-field ${errors.trip_id ? 'input-error' : ''}`} value={form.trip_id} onChange={e => set('trip_id', e.target.value)}>
          <option value="">Select active trip…</option>
          {trips.map(t => (
            <option key={t.id} value={t.id}>{t.trip_number} — {t.origin_address?.split(',')[0]} → {t.destination_address?.split(',')[0]}</option>
          ))}
        </select>
        {errors.trip_id && <p className="text-red-500 text-xs mt-1">{errors.trip_id}</p>}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Date & Time</label>
          <input type="datetime-local" className="input-field" value={form.fuel_timestamp} onChange={e => set('fuel_timestamp', e.target.value)} />
        </div>
        <div>
          <label className="label">Fuel Station Name</label>
          <input className="input-field" value={form.fuel_station_name} onChange={e => set('fuel_station_name', e.target.value)} placeholder="Pilot, Flying J…" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="label">Gallons *</label>
          <input type="number" step="0.001" className={`input-field ${errors.gallons ? 'input-error' : ''}`} value={form.gallons} onChange={e => set('gallons', e.target.value)} placeholder="85.500" />
          {errors.gallons && <p className="text-red-500 text-xs mt-1">{errors.gallons}</p>}
        </div>
        <div>
          <label className="label">Price / Gallon</label>
          <input type="number" step="0.001" className="input-field" value={form.price_per_gallon} onChange={e => set('price_per_gallon', e.target.value)} placeholder="3.899" />
        </div>
        <div>
          <label className="label">Total Cost</label>
          <input type="number" step="0.01" className="input-field" value={form.total_cost} onChange={e => set('total_cost', e.target.value)} placeholder="Auto-calculated" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Location Address</label>
          <input className="input-field" value={form.location_address} onChange={e => set('location_address', e.target.value)} placeholder="Pilot Travel Center, Midland TX" />
        </div>
        <div>
          <label className="label">Odometer Reading</label>
          <input type="number" className="input-field" value={form.odometer_reading} onChange={e => set('odometer_reading', e.target.value)} />
        </div>
      </div>
      <div>
        <label className="label">Payment Method</label>
        <select className="input-field" value={form.payment_method} onChange={e => set('payment_method', e.target.value)}>
          {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
        </select>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? 'Saving…' : 'Add Fuel Record'}
        </button>
      </div>
    </div>
  );
}

export function FuelPage() {
  const { hasRole } = useAuth();
  const toast = useToast();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch]   = useState('');

  const canAdd = hasRole(['administrator', 'fleet_manager', 'dispatcher', 'driver']);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fuelApi.list();
      setRecords(Array.isArray(data) ? data : data?.data || []);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    const record = await fuelApi.create(form);
    setRecords(prev => [record, ...prev]);
    setShowForm(false);
    toast.success('Fuel record added');
  };

  const filtered = records.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return r.trip_number?.toLowerCase().includes(s) ||
           r.unit_number?.toLowerCase().includes(s) ||
           r.driver_name?.toLowerCase().includes(s) ||
           r.fuel_station_name?.toLowerCase().includes(s);
  });

  const totalGallons = filtered.reduce((s, r) => s + parseFloat(r.gallons || 0), 0);
  const totalCost    = filtered.reduce((s, r) => s + parseFloat(r.total_cost || 0), 0);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Fuel Records</h1>
            <p className="text-sm text-gray-500">
              {filtered.length} records · {totalGallons.toFixed(1)} gal · ${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2 })} total
            </p>
          </div>
          {canAdd && (
            <button onClick={() => setShowForm(true)} className="btn-primary">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Fuel Record
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="relative max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" className="input-field pl-9 py-2" placeholder="Search by trip, vehicle, driver…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="card overflow-hidden">
          {loading ? <TableSkeleton rows={6} cols={7} /> : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No fuel records found</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="table-header">Date</th>
                  <th className="table-header">Trip</th>
                  <th className="table-header">Driver</th>
                  <th className="table-header">Vehicle</th>
                  <th className="table-header">Station</th>
                  <th className="table-header">Gallons</th>
                  <th className="table-header">Price/gal</th>
                  <th className="table-header">Total Cost</th>
                  <th className="table-header">Payment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                    <td className="table-cell text-gray-600">{r.fuel_timestamp ? new Date(r.fuel_timestamp).toLocaleDateString() : '—'}</td>
                    <td className="table-cell font-semibold text-blue-600">{r.trip_number || '—'}</td>
                    <td className="table-cell">{r.driver_name || '—'}</td>
                    <td className="table-cell font-medium">{r.unit_number || '—'}</td>
                    <td className="table-cell">{r.fuel_station_name || r.location_address?.split(',')[0] || '—'}</td>
                    <td className="table-cell font-medium">{parseFloat(r.gallons || 0).toFixed(3)}</td>
                    <td className="table-cell">{r.price_per_gallon ? `$${parseFloat(r.price_per_gallon).toFixed(3)}` : '—'}</td>
                    <td className="table-cell font-semibold text-gray-900">{r.total_cost ? `$${parseFloat(r.total_cost).toFixed(2)}` : '—'}</td>
                    <td className="table-cell capitalize text-xs">{r.payment_method?.replace(/_/g,' ')}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td colSpan={5} className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Totals</td>
                  <td className="px-4 py-2 text-sm font-bold text-gray-900">{totalGallons.toFixed(1)}</td>
                  <td className="px-4 py-2" />
                  <td className="px-4 py-2 text-sm font-bold text-gray-900">${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-2" />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Add Fuel Record" size="md">
        <FuelForm onSave={handleCreate} onClose={() => setShowForm(false)} />
      </Modal>
    </div>
  );
}
